import { HttpMethod } from 'constants/'
import { useCallback } from 'react'
import { Query, QueryResponse, QueryResponseCamelCase } from 'types'
import { identity, pick } from 'utils'

import { useRequest } from '../useRequest'
import { useServiceCountryUrl } from '../useServiceCountryUrl'
import { Comment, InteractionType, Note, NoteServiceNoteType, RelatedEntityType } from './types'

enum ApiPathname {
  NOTES = `/api/v1/notes`,
  NOTE = `/api/v1/notes/:noteId`,
  COMMENTS = '/api/v1/comments',
  COMMENT = '/api/v1/comments/:commentGid',
  PIN = `/api/v1/notes/:noteGid/action/pin`,
  UNPIN = `/api/v1/notes/:noteGid/action/unpin`,
  UNREAD = '/api/v1/notes/me/unread',
}

type NoteActionsQuery = {
  hasInteractionType?: InteractionType
  relatedEntityType?: RelatedEntityType
  type?: NoteServiceNoteType
}

type GetManyNotesQuery = Query<
  Partial<Omit<Note, 'author' | 'content' | 'createdBy' | 'relatedEntityType' | 'updatedBy'> & NoteActionsQuery>
>

type GetUnreadNotesQuery = Query<{
  customerGid?: string
  startFrom?: string
}>

type GetUnreadNotesResponse = QueryResponseCamelCase<Note[]>

type GetManyResponse = QueryResponse<Note[]>

type CreateNoteRequest = Omit<Note, 'createdAt' | 'gid' | 'id' | 'updatedAt' | 'updatedBy' | 'wasEdited'>
type CreateNoteResponse = Note

type UpdateNoteRequest = {
  id: string
  content: string
}

type UpdateNoteResponse = Note

type CreateCommentRequest = Pick<Comment, 'author' | 'content' | 'parentGid' | 'parentType'>

type UpdateCommentRequest = Pick<Comment, 'content' | 'gid'>

type GetManyCommentsQuery = Query<Partial<Pick<Comment, 'gid' | 'parentGid'>>>

type GetManyCommentsResponse = QueryResponse<Comment[]>

interface UseHookContext {
  createNote: (query: CreateNoteRequest) => Promise<CreateNoteResponse>
  getNote: (query: GetManyNotesQuery) => Promise<GetManyResponse>
  updateNote: (query: UpdateNoteRequest) => Promise<UpdateNoteResponse>
  createComment: (query: CreateCommentRequest) => Promise<Comment>
  getComments: (query?: GetManyCommentsQuery) => Promise<GetManyCommentsResponse>
  updateComment: (query: UpdateCommentRequest) => Promise<Comment>
  pinNote: (noteGid: string) => Promise<Note>
  unPinNote: (noteGid: string) => Promise<void>
  getUnreadNotes: (query: GetUnreadNotesQuery) => Promise<GetUnreadNotesResponse>
}

const useNoteService = (): UseHookContext => {
  const { url: baseUrl } = useServiceCountryUrl('note')

  const createNoteRequest = useRequest<CreateNoteRequest, CreateNoteResponse>({
    baseUrl,
    filterQueryToRequestBody: identity,
    method: HttpMethod.POST,
    pathname: ApiPathname.NOTES,
  })

  const updateNoteRequest = useRequest<UpdateNoteRequest, UpdateNoteResponse>({
    baseUrl,
    filterQueryToRequestBody: pick(['content']),
    filterQueryToRouteParams: ({ id: noteId }: UpdateNoteRequest) => ({ noteId }),
    method: HttpMethod.PATCH,
    pathname: ApiPathname.NOTE,
  })

  const createNote = useCallback((request: CreateNoteRequest) => createNoteRequest(request), [createNoteRequest])

  const updateNote = useCallback(
    (request: UpdateNoteRequest) => {
      return updateNoteRequest(request)
    },
    [updateNoteRequest]
  )

  const getNoteRequest = useRequest<GetManyNotesQuery, GetManyResponse>({
    baseUrl,
    filterQueryToSearchParams: identity,
    pathname: ApiPathname.NOTES,
  })

  const getNote = useCallback(
    (query: GetManyNotesQuery) =>
      getNoteRequest({
        ...query,
        sort: 'DESC',
        sortBy: 'createdAt',
      }),
    [getNoteRequest]
  )

  const createCommentRequest = useRequest<CreateCommentRequest, Comment>({
    baseUrl,
    filterQueryToRequestBody: identity,
    method: HttpMethod.POST,
    pathname: ApiPathname.COMMENTS,
  })

  const createComment = useCallback(
    (request: CreateCommentRequest) => createCommentRequest(request),
    [createCommentRequest]
  )

  const getCommentsRequest = useRequest<GetManyCommentsQuery, GetManyCommentsResponse>({
    baseUrl,
    filterQueryToSearchParams: identity,
    pathname: ApiPathname.COMMENTS,
  })

  const getComments = useCallback(
    (request?: GetManyCommentsQuery) => getCommentsRequest(request ?? {}),
    [getCommentsRequest]
  )

  const updateCommentRequest = useRequest<UpdateCommentRequest, Comment>({
    baseUrl,
    filterQueryToRequestBody: pick(['content']),
    filterQueryToRouteParams: ({ gid: commentGid }: UpdateCommentRequest) => ({ commentGid }),
    method: HttpMethod.PATCH,
    pathname: ApiPathname.COMMENT,
  })

  const updateComment = useCallback(
    (request: UpdateCommentRequest) => updateCommentRequest(request),
    [updateNoteRequest]
  )

  const pinANote = useRequest<string, Note>({
    baseUrl,
    filterQueryToRouteParams: (noteGid) => ({ noteGid }),
    method: HttpMethod.POST,
    pathname: ApiPathname.PIN,
  })

  const pinNote = useCallback((noteGid: string) => pinANote(noteGid), [pinANote])

  const unPinANote = useRequest<string, void>({
    baseUrl,
    filterQueryToRouteParams: (noteGid) => ({ noteGid }),
    method: HttpMethod.POST,
    pathname: ApiPathname.UNPIN,
    transformResponse: () => {
      return Promise.resolve()
    },
  })

  const unPinNote = useCallback((noteGid: string) => unPinANote(noteGid), [unPinANote])

  const getUnreadNotesRequest = useRequest<GetUnreadNotesQuery, GetUnreadNotesResponse>({
    baseUrl,
    filterQueryToSearchParams: identity,
    pathname: ApiPathname.UNREAD,
  })

  const getUnreadNotes = useCallback(
    (query: GetUnreadNotesQuery) => getUnreadNotesRequest(query),
    [getUnreadNotesRequest]
  )

  return {
    createComment,
    createNote,
    getComments,
    getNote,
    getUnreadNotes,
    pinNote,
    unPinNote,
    updateComment,
    updateNote,
  }
}

export { NoteServiceNoteType, RelatedEntityType, useNoteService }

export type {
  CreateNoteRequest,
  CreateNoteResponse,
  GetManyNotesQuery,
  UpdateNoteRequest,
  UpdateNoteResponse,
  GetManyCommentsQuery,
  GetManyCommentsResponse,
  CreateCommentRequest,
  UpdateCommentRequest,
  GetUnreadNotesQuery,
  GetUnreadNotesResponse,
}
